import React, { useState } from 'react';
import './KeyValueInput.css';

const KeyValueInput = ({
  title,
  description,
  items = [],
  onChange,
  keyPlaceholder = "키",
  valuePlaceholder = "값",
  allowMultiline = false,
  showMustacheHelper = true
}) => {
  const [newItem, setNewItem] = useState({ key: '', value: '' });

  const handleAddItem = () => {
    if (!newItem.key.trim()) return;

    const item = {
      id: Date.now(),
      key: newItem.key.trim(),
      value: newItem.value
    };

    onChange([...items, item]);
    setNewItem({ key: '', value: '' });
  };

  const handleRemoveItem = (id) => {
    onChange(items.filter(item => item.id !== id));
  };

  const handleUpdateItem = (id, field, value) => {
    onChange(items.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const handleKeyDown = (e, field) => {
    if (e.key === 'Enter' && !e.shiftKey && (!allowMultiline || field === 'key')) {
      e.preventDefault();
      if (field === 'key' && newItem.key.trim()) {
        // 키 입력 완료 시 값 입력란으로 포커스 이동
        const valueInput = e.target.parentElement.parentElement.querySelector('input[name="value"], textarea[name="value"]');
        if (valueInput) valueInput.focus();
      } else if (field === 'value') {
        handleAddItem();
      }
    }
  };

  const insertMustacheVariable = (variableName) => {
    const template = `{{${variableName}}}`;
    const activeElement = document.activeElement;
    if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')) {
      const start = activeElement.selectionStart;
      const end = activeElement.selectionEnd;
      const value = activeElement.value;
      const newValue = value.substring(0, start) + template + value.substring(end);

      // React 방식으로 값 업데이트
      const event = new Event('input', { bubbles: true });
      activeElement.value = newValue;
      activeElement.dispatchEvent(event);

      // 커서를 템플릿 뒤로 이동
      setTimeout(() => {
        activeElement.setSelectionRange(start + template.length, start + template.length);
      }, 0);
    }
  };

  // 사용 가능한 변수 목록 (예시)
  const availableVariables = ['category', 'page', 'userId', 'token'];

  return (
    <div className="keyvalue-container">
      <div>
        <h4 className="keyvalue-title">{title}</h4>
        {description && (
          <p className="keyvalue-description">{description}</p>
        )}
        {showMustacheHelper && (
          <div className="mustache-helper">
            <p className="mustache-helper-title">
              💡 Mustache 문법을 사용할 수 있습니다: <code>{'{{변수명}}'}</code>
            </p>
            <div className="mustache-buttons">
              {availableVariables.map(variable => (
                <button
                  key={variable}
                  type="button"
                  className="mustache-btn"
                  onClick={() => insertMustacheVariable(variable)}
                >
                  {'{{'}{variable}{'}}'}</button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* 기존 아이템 목록 */}
      {items.length > 0 ? (
        <div className="keyvalue-items">
          {items.map((item) => (
            <div key={item.id} className="keyvalue-item">
              <div className="keyvalue-fields">
                <div className="keyvalue-field">
                  <label className="keyvalue-field-label">키</label>
                  <input
                    type="text"
                    value={item.key}
                    onChange={(e) => handleUpdateItem(item.id, 'key', e.target.value)}
                    className="keyvalue-field-input"
                    placeholder={keyPlaceholder}
                  />
                </div>
                <div className="keyvalue-field">
                  <label className="keyvalue-field-label">값</label>
                  {allowMultiline ? (
                    <textarea
                      value={item.value}
                      onChange={(e) => handleUpdateItem(item.id, 'value', e.target.value)}
                      className="keyvalue-field-textarea"
                      placeholder={valuePlaceholder}
                      rows="2"
                    />
                  ) : (
                    <input
                      type="text"
                      value={item.value}
                      onChange={(e) => handleUpdateItem(item.id, 'value', e.target.value)}
                      className="keyvalue-field-input"
                      placeholder={valuePlaceholder}
                    />
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleRemoveItem(item.id)}
                className="keyvalue-remove-btn"
                title="항목 삭제"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-keyvalues">
          <div className="empty-keyvalues-icon">📝</div>
          <div className="empty-keyvalues-text">{title}이 없습니다</div>
          <div className="empty-keyvalues-description">아래에서 추가해주세요.</div>
        </div>
      )}

      {/* 새 아이템 추가 */}
      <div className="add-keyvalue-section">
        <div className="add-keyvalue-fields">
          <div>
            <label className="keyvalue-field-label">키 *</label>
            <input
              type="text"
              name="key"
              value={newItem.key}
              onChange={(e) => setNewItem(prev => ({ ...prev, key: e.target.value }))}
              onKeyDown={(e) => handleKeyDown(e, 'key')}
              className="add-keyvalue-input"
              placeholder={keyPlaceholder}
            />
          </div>
          <div>
            <label className="keyvalue-field-label">값</label>
            {allowMultiline ? (
              <textarea
                name="value"
                value={newItem.value}
                onChange={(e) => setNewItem(prev => ({ ...prev, value: e.target.value }))}
                onKeyDown={(e) => handleKeyDown(e, 'value')}
                className="add-keyvalue-textarea"
                placeholder={valuePlaceholder}
                rows="2"
              />
            ) : (
              <input
                type="text"
                name="value"
                value={newItem.value}
                onChange={(e) => setNewItem(prev => ({ ...prev, value: e.target.value }))}
                onKeyDown={(e) => handleKeyDown(e, 'value')}
                className="add-keyvalue-input"
                placeholder={valuePlaceholder}
              />
            )}
          </div>
          <button
            type="button"
            onClick={handleAddItem}
            disabled={!newItem.key.trim()}
            className="add-keyvalue-btn"
          >
            ➕ 추가
          </button>
        </div>
      </div>
    </div>
  );
};

export default KeyValueInput;